import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Chip,
  Tooltip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch, Divider, MenuItem,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Layers, Pencil, RefreshCw, ShieldAlert, Settings2 } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";
import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const BRAND = "#7A3FE4";
const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtUsd6 = (n) => (n == null ? "—" : `$${Number(n).toFixed(6)}`);

// Plan tiers + the ceiling guardrail. The only discount lever is the token
// price; the per-event schedule is identical across plans (Price card page).
function PlansAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [plans, setPlans]     = useState([]);
  const [ceiling, setCeiling] = useState(null);
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [toast, setToast]     = useState(null);

  const [edit, setEdit]       = useState(null);      // plan being edited
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [dlgError, setDlgError] = useState(null);    // { message, ceiling }

  const [cfgForm, setCfgForm] = useState(null);
  const [users, setUsers]     = useState([]);
  const [renewUser, setRenewUser] = useState("");
  const [renewing, setRenewing]   = useState(false);
  const [renewRes, setRenewRes]   = useState(null);
  useEffect(() => { axiosInstance.get("/ai/admin/routing/targets").then(r => setUsers(r.data.users || [])).catch(() => {}); }, []);
  const doRenew = async () => {
    setRenewing(true); setRenewRes(null);
    try { const { data } = await axiosInstance.post(`/ai/admin/tokens/renew-user/${renewUser}`); setRenewRes(data); }
    catch (e) { setRenewRes({ error: e.response?.data?.error || t("plRenewFailed") }); }
    finally { setRenewing(false); }
  };
  const [cfgSaving, setCfgSaving] = useState(false);

  const showToast = (msg, sev = "success") => { setToast({ msg, sev }); setTimeout(() => setToast(null), 6000); };

  const load = useCallback(() => {
    setLoading(true); setError("");
    Promise.all([axiosInstance.get("/ai/admin/plans"), axiosInstance.get("/ai/admin/price-card?scope=global")])
      .then(([p, c]) => { setPlans(p.data.plans || []); setCeiling(p.data.ceiling || null); setConfig(c.data.config || null); setCfgForm(null); })
      .catch(e => setError(e.response?.data?.error || t("plLoadFailed")))
      .finally(() => setLoading(false));
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (p) => {
    setEdit(p); setDlgError(null);
    setForm({ name: p.name, monthlyPrice: p.monthlyPrice, includedTokens: p.includedTokens ?? 0, topupTokensPer100: p.topupTokensPer100 ?? 100000, seatLimit: p.seatLimit, isActive: !!p.isActive });
  };
  const derivedPrice = form.topupTokensPer100 > 0 ? 100 / Number(form.topupTokensPer100) : null;
  const belowCeiling = derivedPrice != null && ceiling?.ceilingTokenPriceUsd != null && derivedPrice < ceiling.ceilingTokenPriceUsd;

  const save = async (acknowledgeCeiling = false) => {
    setSaving(true); setDlgError(null);
    try {
      await axiosInstance.put(`/ai/admin/plans/${edit.id}`, { ...form, acknowledgeCeiling });
      setEdit(null); load(); showToast(t("plSaved"));
    } catch (e) {
      const d = e.response?.data;
      setDlgError({ message: d?.error || t("plSaveFailed"), code: d?.code, ceiling: d?.ceiling });
    } finally { setSaving(false); }
  };

  const saveConfig = async () => {
    setCfgSaving(true);
    try {
      const body = { ...cfgForm };
      if (typeof body.topupPacks === "string") body.topupPacks = body.topupPacks.split(/[\s,]+/).map(Number).filter(n => n > 0);
      const { data } = await axiosInstance.put("/ai/admin/token-settings", body);
      setConfig(data.config); setCfgForm(null); showToast(t("tsSaved")); load();
    } catch (e) { showToast(e.response?.data?.error || t("tsSaveFailed"), "error"); }
    finally { setCfgSaving(false); }
  };

  const headSx = { fontSize: 12, fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" };
  const cellSx = { fontSize: 13, fontVariantNumeric: "tabular-nums" };
  const card = { p: 2, border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper" };
  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const cfg = cfgForm ?? (config ? {
    tokenUsd: config.tokenUsd, marginDefault: config.marginDefault, marginMin: config.marginMin, marginMax: config.marginMax,
    platformMinUsd: config.platformMinUsd, marginFloor: config.marginFloor, extraSeatUsd: config.extraSeatUsd,
    failoverTokens: Math.round(config.failoverThresholdUsd / config.tokenUsd),
    topupPacks: (config.topupPacks || []).join(", "),
    ackBankEnabled: config.ackBankEnabled !== false,
    billingIncrementSec: config.billingIncrementSec ?? 60,
  } : null);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: alpha(BRAND, 0.12), display: "grid", placeItems: "center" }}><Layers size={20} color={BRAND} /></Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{t("plTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("plSubtitle")}</Typography>
        </Box>
        <Tooltip title={t("refresh")}><IconButton size="small" onClick={load}><RefreshCw size={15} /></IconButton></Tooltip>
      </Box>

      {toast && <Alert severity={toast.sev} sx={{ mb: 2, borderRadius: "10px" }}>{toast.msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      {/* Ceiling guardrail */}
      <Box sx={{ ...card, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}><ShieldAlert size={14} /> {t("plCeilingTitle")}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{t("plCeilingDesc", { floor: Math.round((ceiling?.floor ?? 0.5) * 100) })}</Typography>
        {ceiling && (
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 1.5 }}>
            <Box><Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("plCeilingPrice")}</Typography><Typography sx={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtUsd6(ceiling.ceilingTokenPriceUsd)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("plCeilingMaxPer100")}</Typography><Typography sx={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtInt(ceiling.maxTopupTokensPer100)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t("plCeilingBinding")}</Typography><Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--font-mono)" }}>{ceiling.bindingEvent ?? "—"}</Typography></Box>
          </Box>
        )}
        {ceiling?.events?.length ? (
          <TableContainer><Table size="small">
            <TableHead><TableRow>
              <TableCell sx={headSx}>{t("pcEvent")}</TableCell><TableCell sx={headSx} align="right">{t("pcTokens")}</TableCell>
              <TableCell sx={headSx} align="right">{t("plEventCost")}</TableCell><TableCell sx={headSx}>{t("plCostSource")}</TableCell>
              <TableCell sx={headSx} align="right">{t("plMinTokenPrice")}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {ceiling.events.map(e => (
                <TableRow key={e.event} sx={e.event === ceiling.bindingEvent ? { bgcolor: alpha("#E8930C", 0.08) } : undefined}>
                  <TableCell sx={{ ...cellSx, fontFamily: "var(--font-mono)", fontSize: 12 }}>{e.event}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtInt(e.tokens)}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtUsd6(e.costUsd)}</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{e.source === "cost_profile" ? t("plSourceProfile") : t("plSourceUsage", { n: fmtInt(Math.round(e.actualUnits)) })}</TableCell>
                  <TableCell sx={cellSx} align="right"><b>{fmtUsd6(e.minTokenPriceUsd)}</b></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
        ) : null}
      </Box>

      {/* Tiers */}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper", mb: 2.5 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headSx}>{t("plPlan")}</TableCell><TableCell sx={headSx} align="right">{t("plFee")}</TableCell>
            <TableCell sx={headSx} align="right">{t("plIncluded")}</TableCell><TableCell sx={headSx} align="right">{t("plTopupRate")}</TableCell>
            <TableCell sx={headSx} align="right">{t("plTokenPrice")}</TableCell><TableCell sx={headSx} align="right">{t("plDiscount")}</TableCell>
            <TableCell sx={headSx} align="right">{t("plSeats")}</TableCell><TableCell sx={headSx} align="center">{t("plActive")}</TableCell>
            <TableCell sx={headSx} align="right">{t("rtActions")}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress size={22} /></TableCell></TableRow>}
            {!loading && plans.map(p => {
              const price = p.tokenPriceUsd, tokenUsd = ceiling?.tokenUsd ?? 0.001;
              const below = ceiling?.plans?.find(x => x.id === p.id)?.belowCeiling;
              return (
                <TableRow key={p.id} hover sx={!p.isActive ? { opacity: 0.55 } : undefined}>
                  <TableCell sx={cellSx}><b>{p.name}</b> <span style={{ opacity: 0.6, fontSize: 11 }}>({p.id})</span></TableCell>
                  <TableCell sx={cellSx} align="right">${Number(p.monthlyPrice).toFixed(0)}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtInt(p.includedTokens)}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtInt(p.topupTokensPer100)}</TableCell>
                  <TableCell sx={cellSx} align="right">
                    {fmtUsd6(price)}
                    {below ? <Tooltip title={t("plBelowCeiling")}><Chip size="small" label={t("plBelow")} sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: alpha("#DC2626", 0.12), color: "#B91C1C" }} /></Tooltip> : null}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">{price != null ? `${((1 - price / tokenUsd) * 100).toFixed(1)}%` : "—"}</TableCell>
                  <TableCell sx={cellSx} align="right">{p.seatLimit === -1 ? t("plUnlimited") : p.seatLimit}</TableCell>
                  <TableCell align="center"><Switch size="small" checked={!!p.isActive} onChange={async () => { try { await axiosInstance.put(`/ai/admin/plans/${p.id}`, { isActive: !p.isActive }); load(); } catch (e) { showToast(e.response?.data?.error || t("plSaveFailed"), "error"); } }} /></TableCell>
                  <TableCell align="right"><Tooltip title={t("rtEdit")}><IconButton size="small" onClick={() => openEdit(p)}><Pencil size={14} /></IconButton></Tooltip></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2.5 }}>{t("plSameFeatures")}</Typography>

      {/* Token settings */}
      <Box sx={card}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}><Settings2 size={14} /> {t("tsTitle")}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{t("tsDesc")}</Typography>
        {cfg && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {[
              ["tokenUsd", "tsTokenUsd", 0.000001], ["marginDefault", "tsMarginDefault", 0.01], ["marginMin", "tsMarginMin", 0.01], ["marginMax", "tsMarginMax", 0.01],
              ["platformMinUsd", "tsPlatformMin", 0.0001], ["marginFloor", "tsFloor", 0.01], ["extraSeatUsd", "tsExtraSeat", 1], ["failoverTokens", "tsFailover", 100],
              ["billingIncrementSec", "tsIncrement", 1],
            ].map(([k, label, step]) => (
              <TextField key={k} size="small" type="number" label={t(label)} value={cfg[k] ?? ""} inputProps={{ step }} sx={inputSx}
                onChange={e => setCfgForm({ ...cfg, [k]: e.target.value === "" ? null : Number(e.target.value) })} />
            ))}
          </Box>
        )}
        {cfg && (
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap", mt: 1.5 }}>
            <TextField size="small" label={t("tsPacks")} value={cfg.topupPacks ?? ""} sx={{ ...inputSx, minWidth: 320 }} helperText={t("tsPacksHelp")}
              onChange={e => setCfgForm({ ...cfg, topupPacks: e.target.value })} />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch size="small" checked={!!cfg.ackBankEnabled} onChange={e => setCfgForm({ ...cfg, ackBankEnabled: e.target.checked })} />
                <Typography sx={{ fontSize: 13 }}>{t("tsAckBank")}</Typography>
              </Box>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary", maxWidth: 420 }}>{t("tsAckBankHelp")}</Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" disabled={!cfgForm} onClick={() => setCfgForm(null)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button size="small" variant="contained" disabled={!cfgForm || cfgSaving} onClick={saveConfig} sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {cfgSaving ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : t("tsSave")}
          </Button>
        </Box>
      </Box>

      {/* Renew / migrate a tenant now (the Stripe renewal webhook does this automatically) */}
      <Box sx={{ ...card, mt: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>{t("plRenewTitle")}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{t("plRenewDesc")}</Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField select size="small" label={t("rtTenant")} value={renewUser} onChange={e => setRenewUser(e.target.value)} sx={{ ...inputSx, minWidth: 280 }} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
            {users.map(u => <MenuItem key={u.id} value={String(u.id)}>#{u.id} · {u.email || u.name}{u.plan ? ` · ${u.plan}` : ""}</MenuItem>)}
          </TextField>
          <Button variant="outlined" size="small" onClick={doRenew} disabled={!renewUser || renewing} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
            {renewing ? <CircularProgress size={13} /> : t("plRenew")}
          </Button>
          {renewRes && (
            <Box sx={{ flexBasis: "100%", px: 1.5, py: 1, borderRadius: "10px", bgcolor: alpha(BRAND, 0.06), fontFamily: "var(--font-mono)", fontSize: 12.5 }}>
              {renewRes.error ? <span style={{ color: "#DC2626" }}>{renewRes.error}</span>
                : <span>plan={renewRes.planId} · migrated={String(!!renewRes.migration?.migrated)} migrationTokens={Math.round(renewRes.migration?.grant?.tokens ?? 0).toLocaleString()} ({renewRes.migration?.grant?.unusedIn ?? 0} in / {renewRes.migration?.grant?.unusedOut ?? 0} out min{renewRes.migration?.grant?.route?.label ? ` @ ${renewRes.migration.grant.route.label}` : ""}) · expired={Math.round(renewRes.renewal?.expiredTokens ?? 0).toLocaleString()} · granted={(renewRes.renewal?.grantedTokens ?? 0).toLocaleString()}{renewRes.renewal?.granted === false ? ` (${renewRes.renewal.reason})` : ""}</span>}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit dialog */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{t("plEditTitle")} · {edit?.name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>{t("plEditDesc")}</Typography>
          {dlgError && (
            <Alert severity={(dlgError.code === "below_ceiling" || dlgError.code === "ceiling_unavailable") ? "warning" : "error"} sx={{ mb: 2, borderRadius: "10px" }}
              action={(dlgError.code === "below_ceiling" || dlgError.code === "ceiling_unavailable") ? <Button color="inherit" size="small" onClick={() => save(true)} disabled={saving}>{t("plOverride")}</Button> : null}>
              {dlgError.message}
            </Alert>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField size="small" label={t("plPlan")} value={form.name ?? ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} sx={inputSx} />
            <TextField size="small" type="number" label={t("plFee")} value={form.monthlyPrice ?? ""} onChange={e => setForm(f => ({ ...f, monthlyPrice: e.target.value }))} sx={inputSx} />
            <TextField size="small" type="number" label={t("plIncluded")} value={form.includedTokens ?? ""} onChange={e => setForm(f => ({ ...f, includedTokens: e.target.value }))} sx={inputSx} helperText={t("plIncludedHelp")} />
            <TextField size="small" type="number" label={t("plTopupRate")} value={form.topupTokensPer100 ?? ""} onChange={e => setForm(f => ({ ...f, topupTokensPer100: e.target.value }))} sx={inputSx}
              error={belowCeiling}
              helperText={derivedPrice != null ? `${t("plTokenPrice")}: ${fmtUsd6(derivedPrice)}${ceiling?.ceilingTokenPriceUsd != null ? ` · ${t("plCeilingShort")} ${fmtUsd6(ceiling.ceilingTokenPriceUsd)}` : ""}` : t("plTopupHelp")} />
            <TextField size="small" type="number" label={t("plSeats")} value={form.seatLimit ?? ""} onChange={e => setForm(f => ({ ...f, seatLimit: e.target.value }))} sx={inputSx} helperText={t("plSeatsHelp")} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Switch size="small" checked={!!form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /><Typography sx={{ fontSize: 13 }}>{t("plActive")}</Typography></Box>
          </Box>
          {belowCeiling && !dlgError && <Alert severity="warning" sx={{ mt: 2, borderRadius: "10px" }}>{t("plBelowCeilingWarn", { price: fmtUsd6(derivedPrice), ceiling: fmtUsd6(ceiling.ceilingTokenPriceUsd), event: ceiling.bindingEvent })}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEdit(null)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={() => save(false)} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {saving ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : t("plSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlansAdmin;
